import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin, Download } from "lucide-react";
import { toast } from "sonner";
import api, { taka, formatApiErrorDetail } from "@/lib/api";
import { downloadOrderInvoice } from "@/lib/invoice";
import { OrderTimeline } from "@/components/store/OrderTimeline";
import { STATUS_LABEL, STATUS_COLOR } from "@/lib/bd";

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNo, setOrderNo] = useState(params.get("order") || "");
  const [mobile, setMobile] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (no) => {
    const code = (no || orderNo).trim();
    if (!code) return;
    if (!mobile.trim()) { toast.error("অর্ডার যাচাইয়ে মোবাইল নম্বর দিন"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/track/${code}`, { params: { mobile: mobile.trim() } });
      setOrder(data);
    } catch (err) {
      setOrder(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {}, []); // require explicit mobile-verified search

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="text-2xl font-bold text-[#1E3A2B] mb-2">অর্ডার ট্র্যাক করুন</h1>
      <p className="text-stone-500 mb-5">নিরাপত্তার জন্য অর্ডার আইডি ও অর্ডারে ব্যবহৃত মোবাইল নম্বর দুটোই দিন</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder="অর্ডার আইডি (ORD-2026-...)" className="flex-1 h-12 px-4 rounded-xl border border-stone-300 bg-white" data-testid="track-input" />
        <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="মোবাইল নম্বর" className="flex-1 h-12 px-4 rounded-xl border border-stone-300 bg-white" data-testid="track-mobile" />
        <button onClick={() => search()} disabled={loading} className="bg-[#1E3A2B] text-white font-medium px-5 h-12 rounded-xl flex items-center justify-center gap-2" data-testid="track-btn">
          <Search size={18} /> খুঁজুন
        </button>
      </div>

      {order && (
        <div className="mt-6 space-y-4">
          <div className="bg-white rounded-2xl border border-stone-200 p-5 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-bold text-[#1E3A2B] text-lg">{order.order_no}</div>
              <div className="text-sm text-stone-500">{order.customer_name} • {order.mobile}</div>
            </div>
            <span className={`text-sm font-semibold px-3 py-1 rounded-full ${STATUS_COLOR[order.status]}`}>{STATUS_LABEL[order.status]}</span>
          </div>
          <button onClick={() => downloadOrderInvoice(order)} data-testid="track-download-invoice" className="w-full bg-[#CA8A04] text-white font-medium h-11 rounded-xl flex items-center justify-center gap-2"><Download size={16} /> ইনভয়েস ডাউনলোড (PDF)</button>
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-bold text-[#1E3A2B] mb-4">ডেলিভারি অবস্থা</h3>
            <OrderTimeline status={order.status} />
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-bold text-[#1E3A2B] mb-2 flex items-center gap-2"><MapPin size={16} /> ঠিকানা</h3>
            <p className="text-sm text-stone-600">{order.address}, {order.upazila}, {order.district}</p>
            <div className="border-t border-stone-200 mt-3 pt-3 space-y-1">
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm"><span className="text-stone-600">{it.name_bn} × {it.quantity}</span><span>{taka(it.line_total)}</span></div>
              ))}
              <div className="flex justify-between font-bold text-[#1E3A2B] pt-1"><span>সর্বমোট</span><span>{taka(order.total)}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
